import { useMemo, useState } from 'react';

export type AiSnapNutrients = Record<string, number | string | null | undefined>;

export type AiSnapItem = {
  name: string;
  grams: number;
  nutrients: AiSnapNutrients;
};

export type AiSnapAnalysis = {
  food_name?: string;
  item_category?: 'food' | 'beverage' | 'alcohol' | string;
  items?: AiSnapItem[];
  totals?: AiSnapNutrients;
  [k: string]: any;
};

const num = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function useAiSnapModel(analysis: AiSnapAnalysis | null) {
  const rawItems = useMemo(
    () => analysis && Array.isArray(analysis.items) ? analysis.items : [],
    [analysis]
  );
  const fallbackItems: AiSnapItem[] = useMemo(
    () => rawItems.length
      ? rawItems
      : analysis
        ? [{ name: String(analysis.food_name || 'AI识别餐食'), grams: 100, nutrients: (analysis.totals || {}) as any }]
        : [],
    [analysis, rawItems]
  );

  const [checked, setChecked] = useState<boolean[]>(() => fallbackItems.map(() => true));
  const [edits, setEdits] = useState<{ name: string; grams: number }[]>(() =>
    fallbackItems.map(it => ({ name: String(it?.name || '食物'), grams: num(it?.grams) || 100 }))
  );

  // when analysis changes, re-init
  // (caller should remount or provide key; we also expose a reset)
  const resetFromAnalysis = () => {
    setChecked(fallbackItems.map(() => true));
    setEdits(fallbackItems.map(it => ({ name: String(it?.name || '食物'), grams: num(it?.grams) || 100 })));
  };

  const displayItems = useMemo(() => {
    return fallbackItems.map((it, idx) => {
      const edit = edits[idx] || { name: String(it?.name || '食物'), grams: num(it?.grams) || 100 };
      const origGrams = num(it?.grams) || 100;
      const grams = Math.max(1, num(edit.grams) || 1);
      const ratio = origGrams > 0 ? grams / origGrams : 1;
      const nutrients = it?.nutrients || {};
      const scaled: Record<string, number> = {};
      Object.keys(nutrients || {}).forEach((k) => {
        scaled[k] = num((nutrients as any)[k]) * ratio;
      });
      return {
        idx,
        checked: checked[idx] !== false,
        name: String(edit.name || it?.name || '食物'),
        grams,
        origGrams,
        ratio,
        nutrients: scaled,
        rawNutrients: nutrients,
      };
    });
  }, [fallbackItems, edits, checked]);

  const selectedItems = useMemo(() => displayItems.filter(it => it.checked), [displayItems]);

  const totals = useMemo(() => {
    const sum: Record<string, number> = {};
    selectedItems.forEach((it) => {
      Object.entries(it.nutrients || {}).forEach(([k, v]) => {
        sum[k] = (sum[k] || 0) + num(v);
      });
    });
    return sum;
  }, [selectedItems]);

  const selectedCount = selectedItems.length;

  return {
    rawItems: fallbackItems,
    checked,
    setChecked,
    edits,
    setEdits,
    displayItems,
    selectedItems,
    totals,
    selectedCount,
    resetFromAnalysis,
  };
}
